import { fetchEntitlement } from '../services/checklist/entitlementApi'

const COMPANY_ROLE = 'ROLE_COMPANY'

/**
 * Roles that keep working with a lapsed subscription: the imobiliária failed to pay, the worker did
 * not, and Hauseful staff must always be able to get in.
 */
const EXEMPT_ROLES = [
  'ROLE_HOST',
  'ROLE_ADMIN',
  'ROLE_SUPPORT',
  'ROLE_HAUSEFUL_FINANCIAL',
  'ROLE_HAUSEFUL_OPERATOR',
  'ROLE_PEOPLE',
  'ROLE_PARTNER',
]

/** One lookup per page load — the answer cannot change without a payment, which reloads anyway. */
let entitlementPromise = null

const MILLISECONDS_IN_A_DAY = 24 * 60 * 60 * 1000

const currentRoles = () => (localStorage.getItem('access_roles') || '').split(',')

const isCompanyUser = () => {
  const roles = currentRoles()
  return roles.includes(COMPANY_ROLE) && !roles.some((role) => EXEMPT_ROLES.includes(role))
}

/** Support looking at a customer's account carries the customer's token; do not paywall the operator. */
const isImpersonating = () => !!localStorage.getItem('impersonator_token')

const isConfigured = () =>
  !!process.env.VUE_APP_CHECKLIST_URL && !!process.env.VUE_APP_CHECKLIST_API_URL

export const paywallUrl = () => {
  const token = localStorage.getItem('access_token')
  const base = process.env.VUE_APP_CHECKLIST_URL
  return `${base}/paywall?accessToken=${encodeURIComponent(token)}`
}

/**
 * The raw entitlement, resolved at most once per page load and shared by every reader. Resolves to
 * null whenever there is nothing to ask about or hcheck could not answer — callers decide what that
 * silence means for them, and none of them may treat it as bad news.
 */
const companyEntitlement = () => {
  if (!isConfigured()) return Promise.resolve(null)
  if (!localStorage.getItem('access_token')) return Promise.resolve(null)
  if (!isCompanyUser() || isImpersonating()) return Promise.resolve(null)

  if (!entitlementPromise) {
    entitlementPromise = fetchEntitlement()
      .then(({ data }) => data || null)
      .catch(() => null)
  }

  return entitlementPromise
}

/**
 * Whether the logged-in imobiliária lost access. Fails open — an unreachable hcheck, a user with no
 * company, or any other error must never keep a paying customer out of the product.
 */
export const isCompanyBlocked = () =>
  companyEntitlement().then((entitlement) => !!entitlement && entitlement.allowed === false)

/**
 * The running free trial, or null when there is none.
 *
 * Shares the one entitlement lookup with the paywall, so surfacing the trial banner costs no extra
 * request. Returns `{ termEnd, daysLeft }`; `daysLeft` floors at 0 rather than going negative, since
 * a trial past its end is over, not owed.
 *
 * Null on every uncertainty — not a company user, hcheck unreachable, no `termEnd`. A trial notice is
 * a claim about the customer's account; inventing one is worse than staying quiet.
 */
export const companyTrial = () =>
  companyEntitlement().then((entitlement) => {
    if (!entitlement || !entitlement.isTrial || !entitlement.termEnd) return null

    const endsAt = new Date(entitlement.termEnd)
    if (Number.isNaN(endsAt.getTime())) return null

    const millisecondsLeft = endsAt.getTime() - Date.now()
    const daysLeft = Math.max(0, Math.ceil(millisecondsLeft / MILLISECONDS_IN_A_DAY))

    return { termEnd: entitlement.termEnd, daysLeft }
  })

/**
 * The paywall screen itself lives in checklist-web. The token rides in the URL because that app reads
 * `accessToken` from the query string on boot, so the customer is not asked to sign in again.
 */
export const redirectToPaywall = () => {
  window.location.replace(paywallUrl())
}

export const resetEntitlementCache = () => {
  entitlementPromise = null
}
